import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import { fullUrl } from '../../api';

/**
 * Post images / GIFs: a responsive grid that opens a full-screen viewer with
 * previous / next navigation (arrow keys + Escape work too).
 */
export default function ImageGallery({ images = [], alt = '' }) {
  const [open, setOpen] = useState(-1);
  const list = (images || []).filter(Boolean);

  const close = useCallback(() => setOpen(-1), []);
  const step = useCallback((dir) => {
    setOpen((i) => (i < 0 ? i : (i + dir + list.length) % list.length));
  }, [list.length]);

  useEffect(() => {
    if (open < 0) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close, step]);

  if (!list.length) return null;

  const gridClass = list.length === 1
    ? 'grid-cols-1'
    : list.length === 2
      ? 'grid-cols-2'
      : 'grid-cols-2 sm:grid-cols-3';

  return (
    <>
      <div className={`grid gap-1.5 rounded-xl overflow-hidden ${gridClass}`}>
        {list.slice(0, 6).map((src, i) => (
          <button
            key={src + i}
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(i); }}
            className="relative block w-full bg-gray-100 dark:bg-gray-800 group"
            style={{ aspectRatio: list.length === 1 ? '16 / 10' : '1 / 1' }}
          >
            <img
              src={fullUrl(src)}
              alt={alt || `image ${i + 1}`}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition group-hover:opacity-95"
            />
            {i === 5 && list.length > 6 && (
              <span className="absolute inset-0 flex items-center justify-center bg-black/55 text-white font-bold text-xl">
                +{list.length - 6}
              </span>
            )}
          </button>
        ))}
      </div>

      {open >= 0 && createPortal(
        <div className="fixed inset-0 z-[1000] bg-black/95 flex items-center justify-center anim-fade-in">
          <button
            type="button"
            onClick={close}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/15 text-white hover:bg-white/25"
            aria-label="Close"
          >
            <FiX className="w-6 h-6" />
          </button>
          {list.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); step(-1); }}
                className="absolute left-3 z-10 p-2 rounded-full bg-white/15 text-white hover:bg-white/25"
                aria-label="Previous"
              >
                <FiChevronLeft className="w-7 h-7" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); step(1); }}
                className="absolute right-3 z-10 p-2 rounded-full bg-white/15 text-white hover:bg-white/25"
                aria-label="Next"
              >
                <FiChevronRight className="w-7 h-7" />
              </button>
            </>
          )}
          <img
            src={fullUrl(list[open])}
            alt={alt || `image ${open + 1}`}
            className="max-h-[92vh] max-w-[94vw] object-contain anim-pop"
          />
          <span className="absolute bottom-5 text-white/80 text-sm font-medium">
            {open + 1} / {list.length}
          </span>
        </div>,
        document.body,
      )}
    </>
  );
}
